/**
 * Country reference for the dashboard, mirroring the backend's
 * `sanctionsio/countries.py` (`countries_db` / `COUNTRY_LIST` /
 * `COUNTRY_ISO_CODE2_LIST` / `COUNTRY_FORM_CHOICES`) — 251 entries, verified
 * against `develop` on 2026-08-20 (DEV-2505).
 *
 * The backend list is the source of truth: it drives import normalization,
 * filters, search and exports, so `ROWS` carries the backend's names
 * verbatim rather than deriving them from `Intl.DisplayNames`. CLDR is still
 * consulted, but only to widen *matching* — a user typing the modern spelling
 * ("Côte d'Ivoire", "Czechia", "Eswatini", "Türkiye") resolves to the same code
 * the backend labels "Ivory Coast" / "Czech Republic" / "Swaziland" / "Turkey".
 *
 * Non-standard entries kept deliberately, because the backend has them:
 *   AN  ANT  Netherlands Antilles — dissolved 2010, still tagged on old
 *            records. Resolvable and labelled, but withheld from the pickers
 *            (`PICKER_EXCLUDED`) and flagless (`NO_FLAG`): CLDR aliases the
 *            code to "Curaçao", which would duplicate CW, and no flag asset
 *            exists for it.
 *   XK  XK?  Kosovo — placeholder ISO-3, not a real code.
 *   CW  CWU  ISO standard is CUW.
 *   SX  SMX  ISO standard is SXM.
 *
 * When countries.py changes, update `ROWS` — `iso-countries.test.ts`
 * pins the entry count and the legacy entries above.
 */

export type IsoCountry = { iso: string; name: string };

type CountryRow = { iso: string; iso3: string; name: string };

/** The backend country list verbatim, ordered by ISO-2 like `countries_db`. */
const ROWS: CountryRow[] = [
  { iso: "AD", iso3: "AND", name: "Andorra" },
  { iso: "AE", iso3: "ARE", name: "United Arab Emirates" },
  { iso: "AF", iso3: "AFG", name: "Afghanistan" },
  { iso: "AG", iso3: "ATG", name: "Antigua and Barbuda" },
  { iso: "AI", iso3: "AIA", name: "Anguilla" },
  { iso: "AL", iso3: "ALB", name: "Albania" },
  { iso: "AM", iso3: "ARM", name: "Armenia" },
  { iso: "AN", iso3: "ANT", name: "Netherlands Antilles" },
  { iso: "AO", iso3: "AGO", name: "Angola" },
  { iso: "AQ", iso3: "ATA", name: "Antarctica" },
  { iso: "AR", iso3: "ARG", name: "Argentina" },
  { iso: "AS", iso3: "ASM", name: "American Samoa" },
  { iso: "AT", iso3: "AUT", name: "Austria" },
  { iso: "AU", iso3: "AUS", name: "Australia" },
  { iso: "AW", iso3: "ABW", name: "Aruba" },
  { iso: "AX", iso3: "ALA", name: "Åland Islands" },
  { iso: "AZ", iso3: "AZE", name: "Azerbaijan" },
  { iso: "BA", iso3: "BIH", name: "Bosnia and Herzegovina" },
  { iso: "BB", iso3: "BRB", name: "Barbados" },
  { iso: "BD", iso3: "BGD", name: "Bangladesh" },
  { iso: "BE", iso3: "BEL", name: "Belgium" },
  { iso: "BF", iso3: "BFA", name: "Burkina Faso" },
  { iso: "BG", iso3: "BGR", name: "Bulgaria" },
  { iso: "BH", iso3: "BHR", name: "Bahrain" },
  { iso: "BI", iso3: "BDI", name: "Burundi" },
  { iso: "BJ", iso3: "BEN", name: "Benin" },
  { iso: "BL", iso3: "BLM", name: "Saint Barthélemy" },
  { iso: "BM", iso3: "BMU", name: "Bermuda" },
  { iso: "BN", iso3: "BRN", name: "Brunei Darussalam" },
  { iso: "BO", iso3: "BOL", name: "Bolivia" },
  { iso: "BQ", iso3: "BES", name: "Caribbean Netherlands" },
  { iso: "BR", iso3: "BRA", name: "Brazil" },
  { iso: "BS", iso3: "BHS", name: "Bahamas" },
  { iso: "BT", iso3: "BTN", name: "Bhutan" },
  { iso: "BV", iso3: "BVT", name: "Bouvet Island" },
  { iso: "BW", iso3: "BWA", name: "Botswana" },
  { iso: "BY", iso3: "BLR", name: "Belarus" },
  { iso: "BZ", iso3: "BLZ", name: "Belize" },
  { iso: "CA", iso3: "CAN", name: "Canada" },
  { iso: "CC", iso3: "CCK", name: "Cocos (Keeling) Islands" },
  { iso: "CD", iso3: "COD", name: "Congo, The Democratic Republic of the" },
  { iso: "CF", iso3: "CAF", name: "Central African Republic" },
  { iso: "CG", iso3: "COG", name: "Congo" },
  { iso: "CH", iso3: "CHE", name: "Switzerland" },
  { iso: "CI", iso3: "CIV", name: "Ivory Coast" },
  { iso: "CK", iso3: "COK", name: "Cook Islands" },
  { iso: "CL", iso3: "CHL", name: "Chile" },
  { iso: "CM", iso3: "CMR", name: "Cameroon" },
  { iso: "CN", iso3: "CHN", name: "China" },
  { iso: "CO", iso3: "COL", name: "Colombia" },
  { iso: "CR", iso3: "CRI", name: "Costa Rica" },
  { iso: "CU", iso3: "CUB", name: "Cuba" },
  { iso: "CV", iso3: "CPV", name: "Cape Verde" },
  { iso: "CW", iso3: "CWU", name: "Curaçao" },
  { iso: "CX", iso3: "CXR", name: "Christmas Island" },
  { iso: "CY", iso3: "CYP", name: "Cyprus" },
  { iso: "CZ", iso3: "CZE", name: "Czech Republic" },
  { iso: "DE", iso3: "DEU", name: "Germany" },
  { iso: "DJ", iso3: "DJI", name: "Djibouti" },
  { iso: "DK", iso3: "DNK", name: "Denmark" },
  { iso: "DM", iso3: "DMA", name: "Dominica" },
  { iso: "DO", iso3: "DOM", name: "Dominican Republic" },
  { iso: "DZ", iso3: "DZA", name: "Algeria" },
  { iso: "EC", iso3: "ECU", name: "Ecuador" },
  { iso: "EE", iso3: "EST", name: "Estonia" },
  { iso: "EG", iso3: "EGY", name: "Egypt" },
  { iso: "EH", iso3: "ESH", name: "Western Sahara" },
  { iso: "ER", iso3: "ERI", name: "Eritrea" },
  { iso: "ES", iso3: "ESP", name: "Spain" },
  { iso: "ET", iso3: "ETH", name: "Ethiopia" },
  { iso: "FI", iso3: "FIN", name: "Finland" },
  { iso: "FJ", iso3: "FJI", name: "Fiji" },
  { iso: "FK", iso3: "FLK", name: "Falkland Islands" },
  { iso: "FM", iso3: "FSM", name: "Micronesia" },
  { iso: "FO", iso3: "FRO", name: "Faroe Islands" },
  { iso: "FR", iso3: "FRA", name: "France" },
  { iso: "GA", iso3: "GAB", name: "Gabon" },
  { iso: "GB", iso3: "GBR", name: "United Kingdom" },
  { iso: "GD", iso3: "GRD", name: "Grenada" },
  { iso: "GE", iso3: "GEO", name: "Georgia" },
  { iso: "GF", iso3: "GUF", name: "French Guiana" },
  { iso: "GG", iso3: "GGY", name: "Guernsey" },
  { iso: "GH", iso3: "GHA", name: "Ghana" },
  { iso: "GI", iso3: "GIB", name: "Gibraltar" },
  { iso: "GL", iso3: "GRL", name: "Greenland" },
  { iso: "GM", iso3: "GMB", name: "Gambia" },
  { iso: "GN", iso3: "GIN", name: "Guinea" },
  { iso: "GP", iso3: "GLP", name: "Guadeloupe" },
  { iso: "GQ", iso3: "GNQ", name: "Equatorial Guinea" },
  { iso: "GR", iso3: "GRC", name: "Greece" },
  { iso: "GS", iso3: "SGS", name: "South Georgia and the South Sandwich Islands" },
  { iso: "GT", iso3: "GTM", name: "Guatemala" },
  { iso: "GU", iso3: "GUM", name: "Guam" },
  { iso: "GW", iso3: "GNB", name: "Guinea-Bissau" },
  { iso: "GY", iso3: "GUY", name: "Guyana" },
  { iso: "HK", iso3: "HKG", name: "Hong Kong" },
  { iso: "HM", iso3: "HMD", name: "Heard Island and McDonald Islands" },
  { iso: "HN", iso3: "HND", name: "Honduras" },
  { iso: "HR", iso3: "HRV", name: "Croatia" },
  { iso: "HT", iso3: "HTI", name: "Haiti" },
  { iso: "HU", iso3: "HUN", name: "Hungary" },
  { iso: "ID", iso3: "IDN", name: "Indonesia" },
  { iso: "IE", iso3: "IRL", name: "Ireland" },
  { iso: "IL", iso3: "ISR", name: "Israel" },
  { iso: "IM", iso3: "IMN", name: "Isle of Man" },
  { iso: "IN", iso3: "IND", name: "India" },
  { iso: "IO", iso3: "IOT", name: "British Indian Ocean Territory" },
  { iso: "IQ", iso3: "IRQ", name: "Iraq" },
  { iso: "IR", iso3: "IRN", name: "Iran" },
  { iso: "IS", iso3: "ISL", name: "Iceland" },
  { iso: "IT", iso3: "ITA", name: "Italy" },
  { iso: "JE", iso3: "JEY", name: "Jersey" },
  { iso: "JM", iso3: "JAM", name: "Jamaica" },
  { iso: "JO", iso3: "JOR", name: "Jordan" },
  { iso: "JP", iso3: "JPN", name: "Japan" },
  { iso: "KE", iso3: "KEN", name: "Kenya" },
  { iso: "KG", iso3: "KGZ", name: "Kyrgyzstan" },
  { iso: "KH", iso3: "KHM", name: "Cambodia" },
  { iso: "KI", iso3: "KIR", name: "Kiribati" },
  { iso: "KM", iso3: "COM", name: "Comoros" },
  { iso: "KN", iso3: "KNA", name: "Saint Kitts and Nevis" },
  { iso: "KP", iso3: "PRK", name: "North Korea" },
  { iso: "KR", iso3: "KOR", name: "South Korea" },
  { iso: "KW", iso3: "KWT", name: "Kuwait" },
  { iso: "KY", iso3: "CYM", name: "Cayman Islands" },
  { iso: "KZ", iso3: "KAZ", name: "Kazakhstan" },
  { iso: "LA", iso3: "LAO", name: "Laos" },
  { iso: "LB", iso3: "LBN", name: "Lebanon" },
  { iso: "LC", iso3: "LCA", name: "Saint Lucia" },
  { iso: "LI", iso3: "LIE", name: "Liechtenstein" },
  { iso: "LK", iso3: "LKA", name: "Sri Lanka" },
  { iso: "LR", iso3: "LBR", name: "Liberia" },
  { iso: "LS", iso3: "LSO", name: "Lesotho" },
  { iso: "LT", iso3: "LTU", name: "Lithuania" },
  { iso: "LU", iso3: "LUX", name: "Luxembourg" },
  { iso: "LV", iso3: "LVA", name: "Latvia" },
  { iso: "LY", iso3: "LBY", name: "Libya" },
  { iso: "MA", iso3: "MAR", name: "Morocco" },
  { iso: "MC", iso3: "MCO", name: "Monaco" },
  { iso: "MD", iso3: "MDA", name: "Moldova" },
  { iso: "ME", iso3: "MNE", name: "Montenegro" },
  { iso: "MF", iso3: "MAF", name: "Saint Martin (French part)" },
  { iso: "MG", iso3: "MDG", name: "Madagascar" },
  { iso: "MH", iso3: "MHL", name: "Marshall Islands" },
  { iso: "MK", iso3: "MKD", name: "Macedonia" },
  { iso: "ML", iso3: "MLI", name: "Mali" },
  { iso: "MM", iso3: "MMR", name: "Myanmar" },
  { iso: "MN", iso3: "MNG", name: "Mongolia" },
  { iso: "MO", iso3: "MAC", name: "Macao" },
  { iso: "MP", iso3: "MNP", name: "Northern Mariana Islands" },
  { iso: "MQ", iso3: "MTQ", name: "Martinique" },
  { iso: "MR", iso3: "MRT", name: "Mauritania" },
  { iso: "MS", iso3: "MSR", name: "Montserrat" },
  { iso: "MT", iso3: "MLT", name: "Malta" },
  { iso: "MU", iso3: "MUS", name: "Mauritius" },
  { iso: "MV", iso3: "MDV", name: "Maldives" },
  { iso: "MW", iso3: "MWI", name: "Malawi" },
  { iso: "MX", iso3: "MEX", name: "Mexico" },
  { iso: "MY", iso3: "MYS", name: "Malaysia" },
  { iso: "MZ", iso3: "MOZ", name: "Mozambique" },
  { iso: "NA", iso3: "NAM", name: "Namibia" },
  { iso: "NC", iso3: "NCL", name: "New Caledonia" },
  { iso: "NE", iso3: "NER", name: "Niger" },
  { iso: "NF", iso3: "NFK", name: "Norfolk Island" },
  { iso: "NG", iso3: "NGA", name: "Nigeria" },
  { iso: "NI", iso3: "NIC", name: "Nicaragua" },
  { iso: "NL", iso3: "NLD", name: "Netherlands" },
  { iso: "NO", iso3: "NOR", name: "Norway" },
  { iso: "NP", iso3: "NPL", name: "Nepal" },
  { iso: "NR", iso3: "NRU", name: "Nauru" },
  { iso: "NU", iso3: "NIU", name: "Niue" },
  { iso: "NZ", iso3: "NZL", name: "New Zealand" },
  { iso: "OM", iso3: "OMN", name: "Oman" },
  { iso: "PA", iso3: "PAN", name: "Panama" },
  { iso: "PE", iso3: "PER", name: "Peru" },
  { iso: "PF", iso3: "PYF", name: "French Polynesia" },
  { iso: "PG", iso3: "PNG", name: "Papua New Guinea" },
  { iso: "PH", iso3: "PHL", name: "Philippines" },
  { iso: "PK", iso3: "PAK", name: "Pakistan" },
  { iso: "PL", iso3: "POL", name: "Poland" },
  { iso: "PM", iso3: "SPM", name: "Saint Pierre and Miquelon" },
  { iso: "PN", iso3: "PCN", name: "Pitcairn Islands" },
  { iso: "PR", iso3: "PRI", name: "Puerto Rico" },
  { iso: "PS", iso3: "PSE", name: "Palestine" },
  { iso: "PT", iso3: "PRT", name: "Portugal" },
  { iso: "PW", iso3: "PLW", name: "Palau" },
  { iso: "PY", iso3: "PRY", name: "Paraguay" },
  { iso: "QA", iso3: "QAT", name: "Qatar" },
  { iso: "RE", iso3: "REU", name: "Réunion" },
  { iso: "RO", iso3: "ROU", name: "Romania" },
  { iso: "RS", iso3: "SRB", name: "Serbia" },
  { iso: "RU", iso3: "RUS", name: "Russia" },
  { iso: "RW", iso3: "RWA", name: "Rwanda" },
  { iso: "SA", iso3: "SAU", name: "Saudi Arabia" },
  { iso: "SB", iso3: "SLB", name: "Solomon Islands" },
  { iso: "SC", iso3: "SYC", name: "Seychelles" },
  { iso: "SD", iso3: "SDN", name: "Sudan" },
  { iso: "SE", iso3: "SWE", name: "Sweden" },
  { iso: "SG", iso3: "SGP", name: "Singapore" },
  { iso: "SH", iso3: "SHN", name: "Saint Helena" },
  { iso: "SI", iso3: "SVN", name: "Slovenia" },
  { iso: "SJ", iso3: "SJM", name: "Svalbard and Jan Mayen" },
  { iso: "SK", iso3: "SVK", name: "Slovakia" },
  { iso: "SL", iso3: "SLE", name: "Sierra Leone" },
  { iso: "SM", iso3: "SMR", name: "San Marino" },
  { iso: "SN", iso3: "SEN", name: "Senegal" },
  { iso: "SO", iso3: "SOM", name: "Somalia" },
  { iso: "SR", iso3: "SUR", name: "Suriname" },
  { iso: "SS", iso3: "SSD", name: "South Sudan" },
  { iso: "ST", iso3: "STP", name: "São Tomé and Príncipe" },
  { iso: "SV", iso3: "SLV", name: "El Salvador" },
  { iso: "SX", iso3: "SMX", name: "Sint Maarten" },
  { iso: "SY", iso3: "SYR", name: "Syria" },
  { iso: "SZ", iso3: "SWZ", name: "Swaziland" },
  { iso: "TC", iso3: "TCA", name: "Turks and Caicos Islands" },
  { iso: "TD", iso3: "TCD", name: "Chad" },
  { iso: "TF", iso3: "ATF", name: "French Southern Territories" },
  { iso: "TG", iso3: "TGO", name: "Togo" },
  { iso: "TH", iso3: "THA", name: "Thailand" },
  { iso: "TJ", iso3: "TJK", name: "Tajikistan" },
  { iso: "TK", iso3: "TKL", name: "Tokelau" },
  { iso: "TL", iso3: "TLS", name: "East Timor" },
  { iso: "TM", iso3: "TKM", name: "Turkmenistan" },
  { iso: "TN", iso3: "TUN", name: "Tunisia" },
  { iso: "TO", iso3: "TON", name: "Tonga" },
  { iso: "TR", iso3: "TUR", name: "Turkey" },
  { iso: "TT", iso3: "TTO", name: "Trinidad and Tobago" },
  { iso: "TV", iso3: "TUV", name: "Tuvalu" },
  { iso: "TW", iso3: "TWN", name: "Taiwan" },
  { iso: "TZ", iso3: "TZA", name: "Tanzania" },
  { iso: "UA", iso3: "UKR", name: "Ukraine" },
  { iso: "UG", iso3: "UGA", name: "Uganda" },
  { iso: "UM", iso3: "UMI", name: "United States Minor Outlying Islands" },
  { iso: "US", iso3: "USA", name: "United States of America" },
  { iso: "UY", iso3: "URY", name: "Uruguay" },
  { iso: "UZ", iso3: "UZB", name: "Uzbekistan" },
  { iso: "VA", iso3: "VAT", name: "Holy See" },
  { iso: "VC", iso3: "VCT", name: "Saint Vincent and the Grenadines" },
  { iso: "VE", iso3: "VEN", name: "Venezuela" },
  { iso: "VG", iso3: "VGB", name: "Virgin Islands" },
  { iso: "VI", iso3: "VIR", name: "United States Virgin Islands" },
  { iso: "VN", iso3: "VNM", name: "Vietnam" },
  { iso: "VU", iso3: "VUT", name: "Vanuatu" },
  { iso: "WF", iso3: "WLF", name: "Wallis and Futuna" },
  { iso: "WS", iso3: "WSM", name: "Samoa" },
  { iso: "XK", iso3: "XK?", name: "Kosovo" },
  { iso: "YE", iso3: "YEM", name: "Yemen" },
  { iso: "YT", iso3: "MYT", name: "Mayotte" },
  { iso: "ZA", iso3: "ZAF", name: "South Africa" },
  { iso: "ZM", iso3: "ZMB", name: "Zambia" },
  { iso: "ZW", iso3: "ZWE", name: "Zimbabwe" },
];

/** Every backend country, keyed by ISO-2 — including the legacy codes. */
const BY_ISO = new Map(ROWS.map((r) => [r.iso, r]));

/** Resolvable and labelled, but never offered as a new choice in a picker. */
const PICKER_EXCLUDED = new Set(["AN"]);

/** Codes with no Twemoji flag asset — rendering one would 404. */
const NO_FLAG = new Set(["AN"]);

const regionNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

/** CLDR's English name for a code, or "" when the runtime has no entry. */
function cldrName(iso: string): string {
  try {
    const name = regionNames?.of(iso);
    return name && name !== iso ? name : "";
  } catch {
    return "";
  }
}

/**
 * Fold a country name to a comparison key: strips diacritics, apostrophes and
 * punctuation, and equates the "and"/"&" and "Saint"/"St." spellings that CLDR
 * and the backend disagree on. Verified collision-free across both name sets.
 */
function nameKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\u2018\u2019']/g, "")
    .replace(/\band\b|&/g, " ")
    .replace(/\bst\.?\b|\bsaint\b/g, "st")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const ISO2_SET = new Set(ROWS.map((r) => r.iso));
const ISO3_TO_ISO2 = new Map(ROWS.map((r) => [r.iso3, r.iso]));

/**
 * Informal names neither list carries but data sources still emit. CLDR gives
 * MM as "Myanmar (Burma)", which is its own key — "Burma" on its own is not.
 */
const EXTRA_ALIASES: Record<string, string> = {
  burma: "MM",
  "great britain": "GB",
  uk: "GB",
  "Ascensión y Tristán de Acuña": "SH",
  "Saint Helena, Ascensión y Tristán de Acuña": "SH",
  "Saint Helena, Ascension and Tristan da Cunha": "SH",
};

/** EXTRA_ALIASES inverted, so the search index can carry them too. */
const ALIASES_BY_ISO = new Map<string, string[]>();
for (const [name, iso] of Object.entries(EXTRA_ALIASES)) {
  ALIASES_BY_ISO.set(iso, [...(ALIASES_BY_ISO.get(iso) ?? []), name]);
}

const NAME_TO_ISO = new Map<string, string>();

function addAlias(name: string, iso: string): void {
  const key = nameKey(name);
  // First writer wins, so the backend's own name always outranks CLDR's.
  if (key && !NAME_TO_ISO.has(key)) NAME_TO_ISO.set(key, iso);
}

for (const r of ROWS) addAlias(r.name, r.iso);
for (const r of ROWS) {
  // AN is skipped: CLDR resolves the dissolved code to "Curaçao" (= CW).
  if (r.iso === "AN") continue;
  const cldr = cldrName(r.iso);
  if (cldr) addAlias(cldr, r.iso);
}
for (const [name, iso] of Object.entries(EXTRA_ALIASES)) addAlias(name, iso);

/**
 * Normalize a stored value to an ISO alpha-2 code. Accepts an ISO-2 code
 * ("DE"), an ISO-3 code ("DEU", including the backend's non-standard "CWU" /
 * "SMX" / "XK?"), or a country name in either the backend's or the modern
 * spelling ("Ivory Coast", "Côte d'Ivoire"). Returns "" if unknown.
 */
export function toIsoCode(value: string | null | undefined): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && ISO2_SET.has(upper)) return upper;
  if (upper.length === 3) {
    const iso2 = ISO3_TO_ISO2.get(upper);
    if (iso2) return iso2;
  }
  return NAME_TO_ISO.get(nameKey(trimmed)) ?? "";
}

/**
 * The backend's display name for a country. Takes a code or a name (callers
 * hand this raw wire values), and returns the input untouched when nothing
 * resolves — never an uppercased mangle of a name it failed to recognize.
 */
export function isoCountryName(value: string): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  const iso = toIsoCode(raw);
  if (!iso) return raw;
  return BY_ISO.get(iso)?.name ?? iso;
}

/** Every selectable country as { iso, name }, sorted by display name. */
export const ISO_COUNTRIES: IsoCountry[] = ROWS.filter((r) => !PICKER_EXCLUDED.has(r.iso))
  .map((r) => ({ iso: r.iso, name: r.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

function searchKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Haystack per country: backend name + CLDR name + both codes, so the picker
 * finds "Eswatini" under the backend's "Swaziland" and vice versa.
 */
const SEARCH_INDEX = new Map(
  ROWS.map((r) => [
    r.iso,
    searchKey(
      [r.name, cldrName(r.iso), r.iso, r.iso3, ...(ALIASES_BY_ISO.get(r.iso) ?? [])].join(" "),
    ),
  ]),
);

/**
 * The search haystack for a country — backend name, CLDR name, both codes and
 * the informal aliases. For generic list widgets that filter on their own
 * (`SearchMultiSelect`) rather than calling `countryMatchesQuery`.
 */
export function countrySearchText(iso: string): string {
  return SEARCH_INDEX.get(iso.toUpperCase()) ?? "";
}

/** Whether a country matches a picker search query (blank query matches all). */
export function countryMatchesQuery(iso: string, query: string): boolean {
  const q = searchKey(query.trim());
  if (!q) return true;
  return (SEARCH_INDEX.get(iso.toUpperCase()) ?? "").includes(q);
}

/** Whether a flag asset exists for this code — false for the legacy codes. */
export function hasCountryFlag(iso: string): boolean {
  if (!iso || iso.length !== 2) return false;
  return !NO_FLAG.has(iso.toUpperCase());
}

const REGIONAL_INDICATOR_BASE = 0x1f1e6 - 65;

function isoToFlag(iso: string): string {
  return [...iso.toUpperCase()]
    .map((c) => String.fromCodePoint(c.charCodeAt(0) + REGIONAL_INDICATOR_BASE))
    .join("");
}

/** Returns the flag emoji for a 2-letter ISO country code. */
export function flagForIso(iso: string): string {
  if (!hasCountryFlag(iso)) return "";
  return isoToFlag(iso);
}
